// The match mode as a closed set, plus the intents the match surface forks on.
//
// WHY this module exists: every fork used to be written negatively — `matchMode !== "ASSESS" &&
// matchMode !== "RANK"` *meant* Optimize (ChallengeStep, PeopleStep), and the label cascades ended
// in an unguarded `: "Optimize"` (MatchWorkflow, MatchModals, HomePage, MatchPage). A mode token
// this build does not know therefore silently WAS Optimize: the team-size stepper appeared, the
// lock toggle appeared, the fully-locked shortcut armed, and the save preview labelled the result
// "Optimieren". Nothing here is defined by exclusion: the token list is the single source of truth
// (design-codec's zod enum is built from it), the intent matrix answers for every mode including
// the unknown one, and both are total — so adding a token stops the build in every place that has
// to decide what the new mode does, instead of handing it Optimize's behaviour by omission.

// Every mode of genesis' immutable Match.matchMode column (genesis #159 / V0035, @adr SPARK-0029-match-three-case-model-optimize-assess-rank@4) that
// THIS build understands.
export const MATCH_MODES = ["OPTIMIZE", "ASSESS", "RANK", "RANK_COMPANIES"] as const;
export type MatchMode = (typeof MATCH_MODES)[number];

// A token spark cannot name. Not hypothetical: genesis grows its enum before spark's schema copy
// catches up (schema:sync copies genesis' BUILD artifact, so a stale copy is the normal state),
// and matchMode is never validated on the way in — parseDesign does not see it (it lives on the
// Match column, not in the design blob), so whatever the wire says lands on MatchData as-is.
// RANK_COMPANIES was exactly this case until spark grew the fourth card; company-to-company
// matching is the next token the concept names and defers, so the case is not closed.
export const UNKNOWN_MATCH_MODE = "UNKNOWN";
// What the UI forks on: a mode this build knows, or the explicit unknown.
export type UiMatchMode = MatchMode | typeof UNKNOWN_MATCH_MODE;

export function isMatchMode(value: unknown): value is MatchMode {
  return typeof value === "string" && (MATCH_MODES as readonly string[]).includes(value);
}

// The one door from the wire (MatchData.matchMode, Match.matchMode) into the forks below.
// null/undefined = a match from before the column existed (@adr SPARK-0029-match-three-case-model-optimize-assess-rank@4) → Optimize, which is what
// those matches have always rendered as. Anything else unrecognised → UNKNOWN, never Optimize.
export function resolveMatchMode(value: string | null | undefined): UiMatchMode {
  if (value == null) return "OPTIMIZE";
  return isMatchMode(value) ? value : UNKNOWN_MATCH_MODE;
}

// What a mode asks of the UI. One question per fork that exists in the match surface, phrased as
// the intent rather than as the mode name, so a call site reads as what it needs and a new mode is
// answered once, here.
interface ModeIntents {
  // Optimize composes a team, so it has a size to set: the team-size stepper in the Anforderungen
  // panel and the team size in the Challenge step's assistant frame (@adr SPARK-0029-match-three-case-model-optimize-assess-rank@4).
  readonly showsTeamControls: boolean;
  // Locking a candidate into the team (#134) — the toggle on a candidate tile, the intro sentence
  // that explains it, the locked count in the assistant frame, and the fully-locked
  // "evaluate this team" shortcut, which is that concept taken to the seat count.
  readonly allowsLockToggle: boolean;
  // The pool is evaluated as given instead of being distributed across seats — the `assessOnly`
  // UI flag (button labels, the pre-solve seat arithmetic, the team-size meta on saved results).
  // Genesis derives its own flag from matchMode the same way (generated types.ts: ASSESS and RANK
  // → assessOnly=true).
  readonly evaluatesFixedPool: boolean;
  // Genesis scores each candidate on its own and returns a per-candidate narrative — which is what
  // the assessment-style regulators (Ausführlichkeit / Bewertungsmaßstab / Ergebnisfokus) steer,
  // and why the requirements section carries the AI signal shadow in this mode.
  readonly ranksCandidates: boolean;
  // The Challenge step describes a role / open position with per-candidate requirements rather
  // than a team project: the "Rolle beschreiben" / "Rollen-Titel" framing and the one-card
  // Rollenanforderungen layout.
  readonly describesRole: boolean;
  // Whether the mode offers the two settings that decide WHICH power numbers a run uses: the
  // score lens and what an unmeasured power counts as (genesis#328,
  // @external-adr GENESIS-0044-a-match-chooses-which-instrument-its-power-scores-come-from,
  // @external-adr GENESIS-0045-an-unmeasured-power-counts-as-zero-unless-the-match-says-neutral).
  // Every person mode offers them. Company ranking branches before the person power fetch and has
  // no team profile or power result; UNKNOWN remains conservative because spark cannot promise
  // how an unrecognised mode interprets either key.
  readonly usesPowerScoreSettings: boolean;
  // WHAT the match is about. Not a fourth boolean, because it is one question with one answer that
  // three surfaces read: whose criteria the Anforderungen card authors (person MatchCondition vs
  // company CompanyMatchCondition — separate genesis hierarchies, see design-codec), whose pool the
  // second step fills, and whose result the third step renders. A company run branches in genesis
  // BEFORE the person path (MatchResultGenerateController): no solver, no model gateway, no team
  // profile, no team size — so every person-shaped control on those surfaces is not merely unused
  // but a claim about people this match never makes.
  readonly subject: MatchSubject;
}

// The two kinds of thing a match can be about. Deliberately not "person" | "company" singular: a
// match is about a pool of them.
export type MatchSubject = "persons" | "companies";

// Total by construction: a new entry in MATCH_MODES makes this literal fail to typecheck until the
// mode has answered every question above. UNKNOWN answers conservatively — it claims no team, no
// locking and no role, and it evaluates its pool as given — because those are the answers that
// cannot silently misrepresent a mode we cannot name.
const MODE_INTENTS: Record<UiMatchMode, ModeIntents> = {
  OPTIMIZE: {
    showsTeamControls: true,
    allowsLockToggle: true,
    evaluatesFixedPool: false,
    ranksCandidates: false,
    describesRole: false,
    usesPowerScoreSettings: true,
    subject: "persons",
  },
  ASSESS: {
    showsTeamControls: false,
    allowsLockToggle: false,
    evaluatesFixedPool: true,
    ranksCandidates: false,
    describesRole: false,
    usesPowerScoreSettings: true,
    subject: "persons",
  },
  RANK: {
    showsTeamControls: false,
    allowsLockToggle: false,
    evaluatesFixedPool: true,
    ranksCandidates: true,
    describesRole: true,
    usesPowerScoreSettings: true,
    subject: "persons",
  },
  // The company arm (concept v14, the company matching model). Every person intent
  // is off, and none of them is off "for now": it has no team to size and no seats to lock into, it
  // does not describe an open position (the card's own words are "Du hast einen Bedarf"), and
  // ranksCandidates is false because that intent means the per-CANDIDATE narrative the
  // assessment-style regulators steer — a company is never a candidate in that sense.
  // evaluatesFixedPool is true for the one reason it is true of Assess and Rank: the pool is
  // judged as given rather than distributed across seats. Genesis derives no assessOnly for this
  // mode at all (it never reaches the solver), and spark never sends the flag, so this answer is
  // read only by spark's own seat arithmetic — where "no seats" is the correct reading.
  RANK_COMPANIES: {
    showsTeamControls: false,
    allowsLockToggle: false,
    evaluatesFixedPool: true,
    ranksCandidates: false,
    describesRole: false,
    usesPowerScoreSettings: false,
    subject: "companies",
  },
  [UNKNOWN_MATCH_MODE]: {
    showsTeamControls: false,
    allowsLockToggle: false,
    evaluatesFixedPool: true,
    ranksCandidates: false,
    describesRole: false,
    usesPowerScoreSettings: false,
    // "persons" is the conservative answer here, and the only one: the person surfaces are what an
    // unnamed token has always rendered, while routing it to the company surfaces would assert
    // that a mode we cannot name is about companies. Being wrong towards the status quo is
    // recoverable; inventing a subject is not.
    subject: "persons",
  },
};

export function showsTeamControls(mode: UiMatchMode): boolean {
  return MODE_INTENTS[mode].showsTeamControls;
}

export function allowsLockToggle(mode: UiMatchMode): boolean {
  return MODE_INTENTS[mode].allowsLockToggle;
}

export function evaluatesFixedPool(mode: UiMatchMode): boolean {
  return MODE_INTENTS[mode].evaluatesFixedPool;
}

export function ranksCandidates(mode: UiMatchMode): boolean {
  return MODE_INTENTS[mode].ranksCandidates;
}

export function describesRole(mode: UiMatchMode): boolean {
  return MODE_INTENTS[mode].describesRole;
}

export function usesPowerScoreSettings(mode: UiMatchMode): boolean {
  return MODE_INTENTS[mode].usesPowerScoreSettings;
}

export function matchSubject(mode: UiMatchMode): MatchSubject {
  return MODE_INTENTS[mode].subject;
}

// The mode's own name, for the read-only pill on the workflow header and the eyebrow on a match
// card in a list. Three call sites shared one `match.workflow.mode${…}` template with an
// `: "Optimize"` fallback; the table is the same mapping without the fallback.
const MODE_LABEL_ID: Record<MatchMode, string> = {
  OPTIMIZE: "match.workflow.modeOptimize",
  ASSESS: "match.workflow.modeAssess",
  RANK: "match.workflow.modeRank",
  // The card's own name, not a terser second one. Rank and the company mode deliberately share the
  // result word ("Rangliste"), so the pill cannot name this mode by its output without naming two
  // modes identically — and the concept hands out exactly one name for this mode.
  RANK_COMPANIES: "match.workflow.modeRankCompanies",
};

// null for a mode this build cannot name: a wrong name is worse than none, and the callers all
// render nothing instead. A new mode brings its own copy with it (that is part of shipping it),
// and the table above makes tsc ask for it.
export function matchModeLabelId(mode: UiMatchMode): string | null {
  return mode === UNKNOWN_MATCH_MODE ? null : MODE_LABEL_ID[mode];
}

// The name of the candidate-pool step, which is named after what the mode fills it with: Rank
// ranks individual candidates, not a team (@adr SPARK-0029-match-three-case-model-optimize-assess-rank@4). This mapping used to be an `isRank` boolean
// inside MatchWorkflow's stepLabelId, which could carry exactly two namings; a third mode's
// naming had nowhere to go, so it would have arrived as "Teamplayer".
const POOL_STEP_LABEL_ID: Record<MatchMode, string> = {
  OPTIMIZE: "match.step.people",
  ASSESS: "match.step.people",
  RANK: "match.step.peopleRank",
  // Named after what it holds, like the other two: this pool holds companies, not people.
  RANK_COMPANIES: "match.step.companies",
};

// UNKNOWN keeps the neutral team-player naming: unlike the mode pill, a stepper label cannot be
// left empty without breaking the step's tap target.
export function poolStepLabelId(mode: UiMatchMode): string {
  return mode === UNKNOWN_MATCH_MODE ? "match.step.people" : POOL_STEP_LABEL_ID[mode];
}

// Exhaustiveness guard for a switch over UiMatchMode that has to produce something a table cannot
// hold (JSX, a formatted string): `default: return assertUnhandledMode(mode)`. Unreachable while
// the types hold — resolveMatchMode is the only producer of a UiMatchMode and is itself total — so
// it exists for the compile error a new mode triggers at every switch, not for the throw.
export function assertUnhandledMode(mode: never): never {
  throw new Error(`Unhandled match mode: ${String(mode)}`);
}
